import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen pt-20 pb-16 px-6" data-testid="privacy-page">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-heading font-light tracking-tight text-foreground mb-4">
            Privacy Policy
          </h1>
          <p className="text-muted-foreground">Effective Date: March 2025 | Last Updated: March 2025</p>
        </div>

        <div className="prose prose-lg max-w-none">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 md:p-12 space-y-8">
            <p className="text-muted-foreground leading-relaxed">
              Your privacy is important to us. This Privacy Policy explains how Psychotherapy with Anvita collects, uses, stores, and protects your personal information when you visit this website, make a booking, or engage with any of our services. By using this website, you consent to the practices described in this policy.
            </p>

            <section>
              <h2 className="text-2xl font-heading font-medium text-foreground mb-4">1. Who We Are</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                This website is operated by Anvita Sethi, a psychologist practising under the brand Psychotherapy with Anvita. For the purposes of this Privacy Policy, Anvita Sethi is the Data Fiduciary responsible for the collection and processing of your personal data.
              </p>
              <div className="bg-secondary/50 rounded-xl p-4">
                <p className="text-foreground font-medium">Anvita Sethi</p>
                <p className="text-muted-foreground">Psychotherapy with Anvita</p>
                <p className="text-muted-foreground">Email: <a href="mailto:therapywithanvitasethi@gmail.com" className="text-primary hover:underline">therapywithanvitasethi@gmail.com</a></p>
                <p className="text-muted-foreground">Jurisdiction: Bengaluru, Karnataka, India</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-medium text-foreground mb-4">2. Information We Collect</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We collect personal information only to the extent necessary to provide our services and operate this website.
              </p>
              
              <h3 className="text-xl font-heading font-medium text-foreground mb-3">2.1 Information You Provide Directly</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li><strong>Identity Information:</strong> Your full name, date of birth, gender, and contact details (email address, phone number)</li>
                <li><strong>Booking Information:</strong> Session preferences, appointment history, and any intake information provided before your first session</li>
                <li><strong>Payment Information:</strong> Transaction references and billing details processed through Razorpay (INR) or PayPal (USD). We do not store full card or bank account details on our systems</li>
                <li><strong>Communications:</strong> Messages, enquiries, or feedback submitted through the website's contact forms or email</li>
                <li><strong>Health-Related Information:</strong> Information you voluntarily share regarding your mental health, history, and therapeutic goals</li>
              </ul>

              <h3 className="text-xl font-heading font-medium text-foreground mb-3">2.2 Information Collected Automatically</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Usage Data:</strong> Pages visited, time spent on the website, browser type, device type, and referring URL</li>
                <li><strong>Cookies and Tracking:</strong> Session cookies, preference cookies, and analytics cookies</li>
                <li><strong>IP Address:</strong> Your IP address, which may be used to infer approximate geographic location for currency and language display purposes</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-medium text-foreground mb-4">3. How We Use Your Information</h2>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Service Delivery:</strong> To schedule, manage, and provide therapy sessions and related services</li>
                <li><strong>Payment Processing:</strong> To facilitate secure payment transactions via Razorpay (INR) or PayPal (USD)</li>
                <li><strong>Client Communication:</strong> To send appointment confirmations, reminders, and follow-up communications</li>
                <li><strong>Legal and Ethical Compliance:</strong> To comply with applicable laws, court orders, or professional ethical obligations</li>
                <li><strong>Website Improvement:</strong> To understand how visitors use the website and improve its content and functionality</li>
                <li><strong>Marketing and Outreach:</strong> With your prior consent, to share updates, resources, or information about new services</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-medium text-foreground mb-4">4. Confidentiality of Therapeutic Information</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                All information shared during therapy sessions is held in strict professional confidence. As a psychologist, Anvita Sethi is bound by the ethical codes of her professional body and the Mental Healthcare Act, 2017.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Confidentiality may be limited only in the following exceptional circumstances:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Where there is imminent risk of serious harm to you or another identifiable person</li>
                <li>Where there is a reasonable concern about the abuse or neglect of a child or vulnerable adult</li>
                <li>Where disclosure is required by a valid court order or applicable Indian law</li>
                <li>Where you have provided explicit written consent for disclosure to a specific third party</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-medium text-foreground mb-4">5. Sharing Your Information</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We do not sell, rent, or trade your personal information to any third party. We may share limited information only in the following circumstances:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Payment Gateways:</strong> Transaction data shared with Razorpay and PayPal solely for payment processing</li>
                <li><strong>Scheduling Tools:</strong> Appointment data shared with integrated scheduling platforms</li>
                <li><strong>Professional Consultation:</strong> With your consent, limited clinical information may be shared for peer consultation purposes</li>
                <li><strong>Legal Requirements:</strong> With courts, law enforcement, or regulatory authorities where required by Indian law</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-medium text-foreground mb-4">6. Data Retention</h2>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Client Therapy Records:</strong> Retained for a minimum of 7 years after the conclusion of the therapeutic relationship</li>
                <li><strong>Booking and Payment Records:</strong> Retained for 7 years for financial and audit purposes</li>
                <li><strong>Website Usage Data:</strong> Anonymised analytics data retained for up to 2 years</li>
                <li><strong>Contact and Enquiry Data:</strong> Retained for 2 years from the date of last contact</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-medium text-foreground mb-4">7. Your Rights</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Under the Digital Personal Data Protection Act, 2023 and applicable ethical standards, you have the following rights:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Right to Access:</strong> To request a copy of the personal data we hold about you</li>
                <li><strong>Right to Correction:</strong> To request that inaccurate or incomplete data be corrected</li>
                <li><strong>Right to Erasure:</strong> To request deletion of your personal data, subject to our legal retention obligations</li>
                <li><strong>Right to Withdraw Consent:</strong> To withdraw consent for processing at any time</li>
                <li><strong>Right to Grievance Redressal:</strong> To raise a complaint about our data processing practices</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                To exercise any of these rights, please contact us at <a href="mailto:therapywithanvitasethi@gmail.com" className="text-primary hover:underline">therapywithanvitasethi@gmail.com</a>. We will respond within 30 days.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-medium text-foreground mb-4">8. Data Security</h2>
              <p className="text-muted-foreground leading-relaxed">
                We take reasonable technical and organisational measures to protect your personal information, including secure HTTPS encryption, access controls, PCI-DSS compliant payment gateways (Razorpay and PayPal), and regular review of our security practices.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-medium text-foreground mb-4">9. Contact Information</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                If you have any concerns or queries regarding this Privacy Policy:
              </p>
              <div className="bg-secondary/50 rounded-xl p-4">
                <p className="text-foreground font-medium">Grievance Officer: Anvita Sethi</p>
                <p className="text-muted-foreground">Email: <a href="mailto:therapywithanvitasethi@gmail.com" className="text-primary hover:underline">therapywithanvitasethi@gmail.com</a></p>
                <p className="text-muted-foreground">Response Time: Within 30 days of receipt</p>
              </div>
            </section>

            <div className="border-t border-border pt-8 mt-8">
              <p className="text-sm text-muted-foreground text-center">
                By using this website, you acknowledge that you have read and understood this Privacy Policy and consent to the collection and use of your information as described herein.
              </p>
              <p className="text-sm text-muted-foreground text-center mt-4">
                © 2025 Psychotherapy with Anvita. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
